<script setup>
import { ref, reactive, onUnmounted } from 'vue'
import SectionTitle from '@weathernews/btos-vuecomponent/with-onebox-common-elements/src/components/SectionTitle.vue'
import { deepCopy } from '@/utils/utils'

const route = useRoute()

// テキストの拡張を制御する
function isEllipsisActive(element) {
  // 要素の表示幅と実際のコンテンツ高さを比較
  return element.offsetHeight < element.scrollHeight
}
const commentRefs = reactive([])
const expandCommentButton = ref([false, false])

const expandComment = function () {
  // コメントのテキストを拡張する
  commentRefs.forEach((itemEl, index) => {
    if (!itemEl) return
    expandCommentButton.value[index] = false // ボタンを非表示にする
    itemEl.style['-webkit-line-clamp'] = 'unset' // CSSのline-clampを解除
  })
}

// props
const props = defineProps({
  shortData: {
    type: Array,
    default: () => []
  },
  cityInfo: {
    type: Object,
    default: () => {}
  }
})

const shortData = deepCopy(props.shortData || [])

// 要素への参照を格納する配列
const shortDataRefs = reactive([])
const scrollContainerRef = ref(null)
const activeTabIndex = ref(0)
const isManualScrolling = ref(false) // スクロールイベントリスナーを無効/有効にするためのフラグ

// スクロールイベントハンドラ
const handleScroll = () => {
  if (isManualScrolling.value) return // 手動スクロール中は処理をスキップ
  if (!scrollContainerRef.value || shortDataRefs.length === 0) return
  // スクロールコンテナの中心位置を計算
  const containerRect = scrollContainerRef.value.getBoundingClientRect()
  const containerCenter = containerRect.left + containerRect.width / 2
  // 最も中心に近い要素のインデックスを見つける
  let closestIndex = 0
  let minDistance = Infinity
  shortDataRefs.forEach((itemEl, index) => {
    if (!itemEl) return
    const itemRect = itemEl.getBoundingClientRect()
    const itemCenter = itemRect.left + itemRect.width / 2
    // 中心間の距離を計算
    const distance = Math.abs(containerCenter - itemCenter)
    if (distance < minDistance) {
      minDistance = distance
      closestIndex = index
    }
  })
  // 最も中心に近い要素を選択
  activeTabIndex.value = closestIndex
}
watch(() => shortData.value, () => {
  shortDataRefs.length = 0
})
onMounted(() => {
  // 日付のスクロールにイベントを登録
  if (scrollContainerRef.value) {
    scrollContainerRef.value.addEventListener('scroll', handleScroll)
    handleScroll()
  }
  // もしクエリで tomorrow を指定されていて、データの右が「明日」の場合
  if (route.query.tomorrow == '1') {
    try {
      const dateIndex2nd = shortData[1]?.dateIndex
      if (dateIndex2nd === '明日') {
        changeActiveTab(1)
      }
    } catch (e) { console.warn(e) }
  }

  // テキストボックスを拡張するボタンを表示するかどうかを決定
  commentRefs.forEach((itemEl, index) => {
    if (!itemEl) return
    // テキストが省略されているかどうかをチェック
    expandCommentButton.value[index] = isEllipsisActive(itemEl)
  })
})
onUnmounted(() => {
  if (scrollContainerRef.value) {
    scrollContainerRef.value.removeEventListener('scroll', handleScroll)
  }
})

const changeActiveTab = function (index) {
  if (!scrollContainerRef.value) return
  // スクロール処理中はhandleScrollを無効化
  isManualScrolling.value = true
  // スクロール位置を決定
  if (index === 0) {
    // 左端へスクロール
    scrollContainerRef.value.scrollTo({
      left: 0,
      behavior: 'smooth'
    })
  } else if (index === 1) {
    // 右端へスクロール
    scrollContainerRef.value.scrollTo({
      left: scrollContainerRef.value.scrollWidth,
      behavior: 'smooth'
    })
  }
  // スクロールアニメーションが完了するのを待ってからhandleScrollを再有効化
  setTimeout(() => {
    isManualScrolling.value = false
    activeTabIndex.value = index // activeTabIndexを更新
  }, 150) // スムーススクロールの完了を決め打ちで待つ
}
</script>

<template>
  <section class="clothesInfo">
    <SectionTitle :title="cityInfo?.cityname+'の服装予報'" />
    <div
      ref="scrollContainerRef"
      class="inner"
    >
      <div class="blocks">
        <div
          v-for="(short, index) in shortData"
          :ref="el => { if (el) shortDataRefs[index] = el }"
          :key="index"
          class="block"
        >
          <h3 class="heading">
            {{ short.dateIndex }}の天気 {{ short.dateStr }}
          </h3>
          <div class="info">
            <div class="row">
              <div class="clothes day">
                <div class="head">
                  <p class="title">
                    昼間
                  </p>
                </div>
                <figure class="image">
                  <img
                    :src="short.clothes.daytime.icon"
                    width="58"
                    height="50"
                    loading="lazy"
                  >
                </figure>
                <p class="text">
                  {{ short.clothes.daytime.name }}
                </p>
              </div>
              <div class="clothes night">
                <div class="head">
                  <p class="title">
                    朝晩
                  </p>
                </div>
                <figure class="image">
                  <img
                    :src="short.clothes.asaban.icon"
                    width="58"
                    height="50"
                    loading="lazy"
                  >
                </figure>
                <p class="text">
                  {{ short.clothes.asaban.name }}
                </p>
              </div>
            </div>
            <div class="row">
              <div class="weather">
                <div class="data">
                  <div class="icon">
                    <figure>
                      <img
                        :src="short.wxdata.wxIcon"
                      >
                    </figure>
                    <p>{{ short.wxdata.wxText }}</p>
                  </div>
                  <div class="tempList">
                    <div class="temp high">
                      <p class="title">
                        最高
                      </p>
                      <p class="value">
                        {{ short.wxdata.MAXT }}℃
                      </p>
                      <p class="diff">
                        [<span>{{ short.wxdata.DIFF_MAXT }}</span>]
                      </p>
                    </div>
                    <div class="temp low">
                      <p class="title">
                        最低
                      </p>
                      <p class="value">
                        {{ short.wxdata.MINT }}℃
                      </p>
                      <p class="diff">
                        [<span>{{ short.wxdata.DIFF_MINT }}</span>]
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="prec">
                <div class="column">
                  <div class="head">
                    <p>降水</p>
                    <p>午前</p>
                  </div>
                  <div class="body">
                    <figure>
                      <img
                        :src="short.wxdata.POP12.am.icon"
                        width="25"
                        height="25"
                        loading="lazy"
                      >
                    </figure>
                    <p>{{ short.wxdata.POP12.am.pop }}<span>%</span></p>
                  </div>
                </div>
                <div class="column">
                  <div class="head">
                    <p>降水</p>
                    <p>午後</p>
                  </div>
                  <div class="body">
                    <figure>
                      <img
                        :src="short.wxdata.POP12.pm.icon"
                        width="25"
                        height="25"
                        loading="lazy"
                      >
                    </figure>
                    <p>{{ short.wxdata.POP12.pm.pop }}<span>%</span></p>
                  </div>
                </div>
              </div>
            </div>
            <div class="box">
              <p
                :ref="el => { if (el) commentRefs[index] = el }"
                class="clothes__comment"
              >
                {{ short.clothes.comment }}
              </p>
              <button
                v-if="expandCommentButton[index]"
                class="button"
                @click="expandComment()"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="buttons">
      <button
        v-for="(short, index) in shortData"
        :key="index"
        :data-checked="index === activeTabIndex"
        class="button"
        @click="changeActiveTab(index)"
      >
        <p>{{ short.dateStr }}</p>
      </button>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.clothesInfo {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .inner {
    width: 100%;
    padding-bottom: 5px;
    overflow-x: scroll;
    scrollbar-color: #3569C0 #F5F5F5;
    scrollbar-width: thin;
  }
  .blocks {
    display: flex;
    align-items: stretch;
    gap: 7px;
    width: 200%;
  }
  .block {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 10px;
    gap: 10px;
    border-radius: 10px;
    background-color: #F5F5F5;
    .heading {
      color: #333;
      text-align: center;
      font-size: 15px;
      font-weight: 600;
      line-height: 1em;
    }
    .info {
      display: flex;
      flex-direction: column;
      gap: 5px;
      flex: 1;
    }
    .row {
      display: flex;
      align-items: stretch;
      gap: 5px;
      & > * {
        flex: 1;
      }
      &:first-child {
        flex: 1;
      }
      &:nth-child(2) {
        min-height: 63px;
    }
    }
    .clothes {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 5px;
      padding-bottom: 5px;
      border-radius: 8px;
      background-color: #FFF;
      overflow: hidden;
      .head {
        display: flex;
        justify-content: center;
        width: 100%;
        padding-block: 5px;
        &:is(.night *) {
          background-color: #e8e8ef;
        }
        &:is(.day *) {
          background-color: #fafada;
        }
      }
      .title {
        text-align: center;
        font-size: 13px;
        font-weight: 500;
        line-height: 15px;
        background-position: right center;
        background-repeat: no-repeat;
        background-size: contain;
        &:is(.night *) {
          color: #3569C0;
          padding-right: 20px;
          background-image: url('https://gvs.weathernews.jp/clothes/images/icon/icon_night.svg');
        }
        &:is(.day *) {
          color: #E78142;
          padding-right: 20px;
          background-image: url('https://gvs.weathernews.jp/clothes/images/icon/icon_day.svg');
        }
      }
      .image {
        height: 60px;
        margin-top: auto;
        img {
          width: auto;
          height: 100%;
        }
      }
      .text {
        color: #303030;
        text-align: center;
        font-size: 13px;
        font-weight: 300;
        line-height: 1.25em;
      }
    }
    .weather {
      .data {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        height: 100%;
        padding: 5px 3px;
        border-radius: 8px;
        background-color: #FFF;
      }
      .icon {
        display: flex;
        flex-direction: column;
        align-items: center;
        max-width: 45px;
        figure {
          height: 25px;
          img {
            width: auto;
            height: 100%;
          }
        }
        p {
          color: #606060;
          text-align: center;
          font-size: 12px;
          font-weight: 400;
          line-height: 14px;
        }
      }
      .tempList {
        display: flex;
        flex-direction: column;
        gap: 5px;
        .temp {
          display: flex;
          align-items: center;
          gap: 3px;
          &.high {
            p {
              color: #F64D00;
            }
          }
          &.low {
            p {
              color: #0075F3;
            }
          }
        }
        .title {
          padding-inline: 8px;
          border-radius: 100vmax;
          background-color: #EEF1F8;
          text-align: center;
          padding-inline: 5px;
          width: max(100%, 30px);
          font-size: 10px;
          font-weight: 600;
          line-height: 1.4em;
        }
        .value {
          min-width: 35px;
          font-size: 14px;
          font-weight: 600;
          line-height: 1em;
          text-align: right;
        }
        .diff {
          display: flex;
          justify-content: space-between;
          min-width: 22px;
          font-size: 12px;
          font-weight: 300;
          line-height: 14px;
          text-align: right;
        }
      }
    }
    .prec {
      display: flex;
      .column {
        display: flex;
        flex-direction: column;
        flex: 1;
        &:first-child {
          .head {
            border-top-left-radius: 8px;
            border-right: 1px solid #DFE4F0;
          }
          .body {
            border-bottom-left-radius: 8px;
            border-right: 1px solid #DFE4F0;
          }
        }
        &:last-child {
          .head {
            border-top-right-radius: 8px;
          }
          .body {
            border-bottom-right-radius: 8px;
          }
        }
      }
      .head {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        padding: 3px;
        background-color: #fff;
        border-bottom: 1px solid #DFE4F0;
        p {
          &:first-child {
            border-radius: 100vmax;
            background-color: #EEF1F8;
            padding-inline: 8px;
            color: #606060;
            text-align: center;
            font-size: 10px;
            font-weight: 500;
            line-height: 14px;
          }
          &:last-child {
            color: #303030;
            font-size: 12px;
            font-weight: 300;
            line-height: 14px;
          }
        }
      }
      .body {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1;
        gap: 1px;
        padding: 3px;
        background-color: #fff;
        figure {
          width: 25px;
          img {
            width: 100%;
            height: auto;
          }
        }
        p {
          color: #303030;
          font-size: 12px;
          font-weight: 700;
          line-height: 14px;
          span {
            font-size: 10px;
            font-weight: 300;
          }
        }
      }
    }
    .box {
      flex: 1;
      position: relative;
      padding: 10px 15px 10px 10px;
      border-radius: 8px;
      background-color: #FFF;
      p {
        color: #000;
        font-size: 15px;
        font-weight: 300;
        line-height: 1.5em;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 3;
        transition: all 3s ease-in-out;
        &:is(.is-active *) {
          display: block;
          overflow: visible;
        }
      }
      .button {
        position: absolute;
        bottom: 3px;
        right: 3px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background-color: #3569C0;
        &::before,
        &::after {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 10px;
          height: 3px;
          background-color: #fff;
          content: '';
        }
        &::before {
          transform: translate(-50%, -50%);
        }
        &::after {
          transform: translate(-50%, -50%) rotate(90deg);
          transition: transform .2s ease-in-out;
        }
        &:is(.is-active *) {
          &::after {
            transform: translate(-50%, -50%) rotate(0deg);
          }
        }
      }
    }
  }
  .buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    .button {
      display: flex;
      justify-content: center;
      flex: 1;
      padding: 5px;
      border-radius: 100vmax;
      background-color: #E1E8FA;
      p {
        color: #3569C0;
        text-align: center;
        font-size: 15px;
        font-weight: 500;
        line-height: 1em;
      }
      &[data-checked='true'] {
        position: relative;
        background-color: #3569C0;
        &::before {
          position: absolute;
          bottom: 99%;
          left: 50%;
          width: 14px;
          height: 10px;
          margin-left: -7px;
          background-color: #3569C0;
          clip-path: polygon(50% 0, 100% 100%, 0 100%);
          content: '';
        }
        p {
          color: #F9FAF9;
          padding-left: 17px;
          background-image: url('https://gvs.weathernews.jp/clothes/images/icon/icon_check.svg');
          background-position: left center;
          background-repeat: no-repeat;
          background-size: 12px;
        }
      }
    }
  }
  @media (any-hover: hover) {
    .block {
      .box {
        .button {
          cursor: pointer;
          transition: opacity .2s ease-in-out;
          &:hover {
            opacity: .6;
          }
        }
      }
    }
    .buttons {
      .button {
        &:not([data-checked="true"]) {
          cursor: pointer;
          transition: opacity .2s ease-in-out;
          &:hover {
            opacity: .6;
          }
        }
      }
    }
  }
}
.main {
  .clothesInfo {
    @media screen and (min-width: 830px) {
      .blocks {
        gap: 10px;
        width: 135%;
      }
      .block {
        .heading {
          font-size: 18px;
        }
        .clothes {
          gap: 15px;
          .title {
            font-size: 15px;
            line-height: 17px;
            &:is(.night *) {
              padding-right: 40px;
            }
            &:is(.day *) {
              padding-right: 23px;
            }
          }
          .image {
            height: 80px;
          }
          .text {
            font-size: 15px;
          }
        }
        .row {
          &:nth-child(2) {
            min-height: 72px;
          }
        }
        .weather {
          .data {
            padding: 10px;
          }
          .icon {
            gap: 3px;
            max-width: none;
            figure {
              height: 35px;
            }
            p {
              font-size: 13px;
            }
          }
          .tempList {
            .temp {
              justify-content: flex-end;
            }
            .title {
              font-size: 13px;
            }
            .value {
              min-width: 45px;
              font-size: 16px;
            }
            .diff {
              min-width: 32px;
              font-size: 14px;
            }
          }
        }
        .prec {
          .head {
            gap: 5px;
            padding: 5px;
            p {
              &:first-child {
                font-size: 13px;
                line-height: 15px;
              }
              &:last-child {
                font-size: 13px;
                line-height: 15px;
              }
            }
          }
          .body {
            padding: 5px;
            figure {
              width: 30px;
            }
            p {
              font-size: 15px;
              line-height: 17px;
              span {
                font-size: 13px;
              }
            }
          }
        }
      }
      .buttons {
        .button {
          p {
            font-size: 18px;
          }
          &[data-checked='true'] {
            p {
              padding-left: 20px;
              background-size: 14px;
            }
          }
        }
      }
    }
  }
}
</style>
